import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  FormControlLabel,
  IconButton,
  Link,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ReplayIcon from "@mui/icons-material/Replay";
import ndt7 from "@m-lab/ndt7";

type TestStatus = "ready" | "running" | "complete" | "error";
type TestPhase = "connecting" | "download" | "upload";

interface WifiSpeedTestModalProps {
  open: boolean;
  onClose: () => void;
  onUploadSpeed?: (speedMbps: number) => void;
}

const downloadWorkerFile = new URL(
  "@m-lab/ndt7/src/ndt7-download-worker.js",
  import.meta.url,
).toString();
const uploadWorkerFile = new URL(
  "@m-lab/ndt7/src/ndt7-upload-worker.js",
  import.meta.url,
).toString();

const formatSpeed = (value: number | null) =>
  value === null || !Number.isFinite(value) ? "—" : value.toFixed(1);

const WifiSpeedTestModal: React.FC<WifiSpeedTestModalProps> = ({
  open,
  onClose,
  onUploadSpeed,
}) => {
  const [status, setStatus] = useState<TestStatus>("ready");
  const [phase, setPhase] = useState<TestPhase>("connecting");
  const [liveSpeed, setLiveSpeed] = useState(0);
  const [downloadSpeed, setDownloadSpeed] = useState<number | null>(null);
  const [uploadSpeed, setUploadSpeed] = useState<number | null>(null);
  const [acceptedPolicy, setAcceptedPolicy] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const runId = useRef(0);

  useEffect(() => {
    if (!open) {
      runId.current += 1;
      setStatus("ready");
      setPhase("connecting");
      setLiveSpeed(0);
      setDownloadSpeed(null);
      setUploadSpeed(null);
      setAcceptedPolicy(false);
      setErrorMessage("");
    }
  }, [open]);

  const startTest = async () => {
    if (!acceptedPolicy || status === "running") return;

    const currentRun = ++runId.current;
    setStatus("running");
    setPhase("connecting");
    setLiveSpeed(0);
    setDownloadSpeed(null);
    setUploadSpeed(null);
    setErrorMessage("");

    const latestClientSpeed = (measurement: {
      Source: "client" | "server";
      Data: { MeanClientMbps?: number };
    }) => {
      if (currentRun !== runId.current || measurement.Source !== "client") {
        return;
      }
      const speed = measurement.Data.MeanClientMbps;
      if (typeof speed === "number" && Number.isFinite(speed)) {
        setLiveSpeed(speed);
      }
    };

    try {
      const result = await ndt7.test(
        {
          userAcceptedDataPolicy: true,
          metadata: {
            client_name: "MMS Venue WiFi Speed Test",
            client_version: "1.0.0",
          },
          downloadworkerfile: downloadWorkerFile,
          uploadworkerfile: uploadWorkerFile,
        },
        {
          serverDiscovery: () => setPhase("connecting"),
          downloadStart: () => {
            if (currentRun === runId.current) {
              setPhase("download");
              setLiveSpeed(0);
            }
          },
          downloadMeasurement: latestClientSpeed,
          downloadComplete: ({ LastClientMeasurement }) => {
            if (currentRun !== runId.current) return;
            const speed = LastClientMeasurement?.MeanClientMbps;
            if (typeof speed === "number" && Number.isFinite(speed)) {
              setDownloadSpeed(speed);
              setLiveSpeed(speed);
            }
          },
          uploadStart: () => {
            if (currentRun === runId.current) {
              setPhase("upload");
              setLiveSpeed(0);
            }
          },
          uploadMeasurement: latestClientSpeed,
          uploadComplete: ({ LastClientMeasurement }) => {
            if (currentRun !== runId.current) return;
            const speed = LastClientMeasurement?.MeanClientMbps;
            if (typeof speed === "number" && Number.isFinite(speed)) {
              setUploadSpeed(speed);
              setLiveSpeed(speed);
              onUploadSpeed?.(speed);
            }
          },
          error: (message) => {
            if (currentRun !== runId.current) return;
            setErrorMessage(message || "The speed test could not be completed.");
            setStatus("error");
          },
        },
      );

      if (currentRun !== runId.current) return;
      if (result !== 0) {
        setErrorMessage((current) => current || "The speed test could not be completed.");
        setStatus("error");
      } else {
        setStatus("complete");
      }
    } catch (error) {
      if (currentRun !== runId.current) return;
      setErrorMessage(
        error instanceof Error
          ? error.message
          : "Could not connect to the speed test server.",
      );
      setStatus("error");
    }
  };

  const phaseLabel =
    phase === "connecting"
      ? "Connecting to a nearby test server"
      : phase === "download"
        ? "Testing download speed"
        : "Testing upload speed";

  return (
    <Dialog
      open={open}
      onClose={status === "running" ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3, bgcolor: "#fff", color: "#181818" } }}
    >
      <IconButton
        aria-label="Close speed test"
        onClick={onClose}
        disabled={status === "running"}
        sx={{ position: "absolute", top: 10, right: 10, zIndex: 1 }}
      >
        <CloseIcon />
      </IconButton>
      <DialogContent
        sx={{
          px: { xs: 3, sm: 6 },
          py: { xs: 5, sm: 6 },
          minHeight: 470,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        <Box aria-hidden sx={{ width: 104, height: 62, position: "relative", mb: 1 }}>
          <Box sx={{ position: "absolute", inset: 0, border: "8px solid #ed0011", borderBottom: 0, borderRadius: "100px 100px 0 0" }} />
          <Box sx={{ position: "absolute", left: 16, right: 16, top: 15, height: 39, border: "7px solid #ed0011", borderBottom: 0, borderRadius: "80px 80px 0 0" }} />
          <Box sx={{ position: "absolute", left: 32, right: 32, top: 31, height: 23, border: "6px solid #ed0011", borderBottom: 0, borderRadius: "50px 50px 0 0" }} />
          <Box sx={{ position: "absolute", height: 42, width: 5, bgcolor: "#111", left: "calc(50% - 2px)", bottom: 7, borderRadius: 3, transform: "rotate(5deg)", transformOrigin: "bottom center" }} />
          <Box sx={{ position: "absolute", width: 14, height: 14, borderRadius: "50%", bgcolor: "#111", left: "calc(50% - 7px)", bottom: 0 }} />
        </Box>
        <Typography sx={{ fontSize: 38, lineHeight: 1, fontWeight: 800, letterSpacing: -2, mb: 3 }}>
          SPEED TEST
        </Typography>

        <Typography sx={{ fontSize: { xs: 19, sm: 23 }, fontWeight: 600, mb: 1 }}>
          {status === "complete"
            ? "Your internet speed is"
            : status === "running"
              ? phaseLabel
              : status === "error"
                ? "Speed test unavailable"
                : "Test your WiFi speed"}
        </Typography>

        {status === "error" ? (
          <Typography color="text.secondary" sx={{ mb: 2, maxWidth: 360 }}>
            {errorMessage}
          </Typography>
        ) : (
          <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "center", minHeight: 132, mb: 1 }}>
            <Typography
              aria-live="polite"
              sx={{ fontSize: { xs: 100, sm: 138 }, lineHeight: 1, fontWeight: 300, letterSpacing: -8, fontVariantNumeric: "tabular-nums" }}
            >
              {status === "ready"
                ? "—"
                : formatSpeed(
                    status === "complete" ? downloadSpeed : liveSpeed,
                  )}
            </Typography>
            <Typography sx={{ fontSize: { xs: 28, sm: 38 }, fontWeight: 700, mt: 1.5, ml: 1 }}>
              Mbps
            </Typography>
          </Box>
        )}

        {status === "running" && <CircularProgress size={24} sx={{ color: "#ed0011", my: 1 }} />}

        {(status === "complete" || status === "error") && (
          <Box sx={{ display: "flex", gap: 3, mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Download <b>{formatSpeed(downloadSpeed)} Mbps</b>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Upload <b>{formatSpeed(uploadSpeed)} Mbps</b>
            </Typography>
          </Box>
        )}

        {status === "ready" && (
          <FormControlLabel
            sx={{ mt: 1, mb: 1, alignItems: "flex-start", textAlign: "left" }}
            control={
              <Checkbox
                checked={acceptedPolicy}
                onChange={(event) => setAcceptedPolicy(event.target.checked)}
                size="small"
                sx={{ pt: 0.25, color: "#ed0011", "&.Mui-checked": { color: "#ed0011" } }}
              />
            }
            label={
              <Typography variant="caption" color="text.secondary">
                I understand M-Lab collects my public IP address and network measurements, publishes test data, and retains it indefinitely. Read the{" "}
                <Link href="https://www.measurementlab.net/privacy-v3/" target="_blank" rel="noopener noreferrer">
                  M-Lab privacy policy
                </Link>
                .
              </Typography>
            }
          />
        )}

        {status === "running" ? (
          <Typography variant="caption" color="text.secondary">
            Keep this window open while we measure your connection.
          </Typography>
        ) : (
          <Button
            onClick={startTest}
            disabled={status === "ready" && !acceptedPolicy}
            variant="outlined"
            aria-label={status === "ready" ? "Start speed test" : "Run speed test again"}
            sx={{
              mt: 1.5,
              minWidth: 58,
              width: 58,
              height: 58,
              borderRadius: "50%",
              borderWidth: 2,
              borderColor: status === "ready" && !acceptedPolicy ? "#bdbdbd" : "#17b82c",
              color: status === "ready" && !acceptedPolicy ? "#bdbdbd" : "#181818",
              "&:hover": { borderWidth: 2, borderColor: "#17b82c", bgcolor: "#f7fff8" },
            }}
          >
            <ReplayIcon sx={{ fontSize: 30 }} />
          </Button>
        )}

        {status === "ready" && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            Powered by Measurement Lab NDT7
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default WifiSpeedTestModal;
